<template>
  <div class="chart resale_status_purchase" :ref="[refName]"></div>
</template>

<script>
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { numberWithCommas } from 'ops2clientlibrary/src/library/regexp'
import {CHART_COLORS} from '../../code/CodeChart'

export default {
  name: 'srline-chart',
  props: {
    chartData: {
      type: Array,
      default: () => []
    },
    filter: {
      type: Array,
      default: () => []
    },
    refName: {
      type: String,
      required: true
    },
    stacked: {
      type: Boolean,
      default: false
    },
    numberFormatter: {
      type: Object,
      default : () => {
        return {
          numberFormat:"#a",
          bigNumberPrefixes: [
            { "number": 1e+3, "suffix": "천" },
            { "number": 1e+4, "suffix": "만" },
            { "number": 1e+8, "suffix": "억" }
          ]
        }
      }
    },
    numberPrefix: {
      type: String, 
      default: "₩"
    },
    useMinGridDistance: {
      type: Boolean,
      default: true
    },
    mouseCursorStyle: {
      type: Array,
      default: am4core.MouseCursorStyle.pointer
    },
    showColumnTotal: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      CHART_COLORS : CHART_COLORS,
      chart: null,
      colorFilter: {}
    }
  },
  computed: {
    chartFilter () {
      return this.filter.map(item => typeof(item) === 'string' ? item : item.column)
    }
  },
  methods: {
    createCategoryAxies () {
      let categoryAxis = this.chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = "category";

      // **durationTime 확인필요**
      // var durationTime = this.$session.get("srchartMonthParam");

      // if(durationTime > 7){

      //   categoryAxis.renderer.grid.template.location = 0;

      //   categoryAxis.renderer.labels.template.horizontalCenter = "right";
      //   categoryAxis.renderer.labels.template.verticalCenter = "middle";
      //   categoryAxis.renderer.labels.template.rotation = 270;
      //   categoryAxis.renderer.minHeight = 110;
      // }
      
      categoryAxis.tooltip.disabled = true;
      categoryAxis.renderer.grid.template.disabled = true;
      categoryAxis.snapTooltip = true;
      categoryAxis.fontSize = 12;
      if(this.useMinGridDistance){
        categoryAxis.renderer.minGridDistance = 30;
      }
      
      // 데이터 갯수에 따라 차트 시작지점과 끝 지점의 폭을 줄이거나 늘림
      if (this.chartData.length < 3) {
        categoryAxis.renderer.cellStartLocation = 0.3;
        categoryAxis.renderer.cellEndLocation = 0.7;
      } else {
        categoryAxis.renderer.cellStartLocation = 0.1;
        categoryAxis.renderer.cellEndLocation = 0.9;
      }

      return categoryAxis
    },
    getMinChartValue () {
      const minVal = Math.min(...this.chartData.map(item => Math.min(...Object.values(item).filter(arr => typeof(arr) === 'number'))))
      return minVal < 0 ? minVal : 0;  
    },
    createValueAxis () {
      let valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis())
      valueAxis.tooltip.disabled = true;
      valueAxis.fontSize = 12;
      valueAxis.autoGridCount = false;
      valueAxis.strictMinMax = true
      if (this.chartData) {
        valueAxis.min = this.getMinChartValue();
      }
      let existNo = 'N'
      for (let item of this.chartData) {
        if ('Y' === item.no) {
          existNo = 'Y'
          break
        }
      }
      if("Y" === existNo) {
        valueAxis.min = 0;
        valueAxis.max = 5;
      }
      const maxVal = Math.max(...this.chartData.map(item => Math.max(...Object.values(item).filter(arr => typeof(arr) === 'number'))))
      if(maxVal < 5){
        valueAxis.max = 5;
      }
      return valueAxis
    },
    createTopContainer () {
      let topContainer = this.chart.chartContainer.createChild(am4core.Container);
      topContainer.layout = "absolute";
      topContainer.toBack();
      topContainer.paddingBottom = 15;
      topContainer.width = am4core.percent(100);
      this.chart.chartContainer.height = am4core.percent(100);
    },
    createCursor() {
      let cursor = new am4charts.XYCursor();
      cursor.lineX.disabled = true;
      cursor.lineY.disabled = true;
      return cursor
    },
    createSeries (field, name, color, index) {
      let series = this.chart.series.push(new am4charts.LineSeries());
      series.stacked = false
      series.dataFields.valueY = field.replace(/\./g, "");
      series.dataFields.categoryX = "category";
      series.name = name;
      series.strokeWidth = 1.5;
      series.stroke = color;
      series.fill = color;
      series.showOnInit = false;
      series.tooltip = undefined;
      series.sequencedInterpolation = true;
      if (index === 0) {
        series.tooltip.pointerOrientation = "down";
        series.tooltip.getFillFromObject = false;
        series.tooltip.background.fill = am4core.color("rgba(255, 255, 255, 1)");
        series.tooltip.background.fillOpacity = 0.7;
        series.tooltip.background.cornerRadius = 0;
        series.tooltip.strokeOpacity = 1;
        series.tooltip.interactionsEnabled = true
        series.tooltip.keepTargetHover = true
        series.tooltip.cursorOverStyle = this.mouseCursorStyle
        series.tooltip.hoverOnFocus = true
        series.tooltip.hoverable = true
        series.tooltip.keepTargetHover = true
        series.tooltip.events.on("hit", (ev) => {
          this.$emit('hit', ev)
        })
        // series.tooltip.interactionsEnabled = true
        // series.tooltip.keepTargetHover = true;
      }
      series.connect = true;

      this.createBullet(series)
      series.bullets.getIndex(0).adapter.add('tooltipHTML', (html, target) => {
        let tooltipText = '';
        let categoryX = target.dataItem.categoryX;
        let columnData = target.dataItem.dataContext
        const filter = new Array(...this.chartFilter)
        const total = columnData.total || this.chartFilter.map(item => {
          return columnData[item]
        }).reduce((acc, cur) => acc + cur, 0)
        filter.reverse().forEach((itemKey, index) => {
          let itemLabel = typeof(this.filter[index]) === 'string' ? this.filter[index] : this.filter[index].label
          let itemValue = itemKey.replace(/\./g, "");
          const result = this.chartData.find(item => item.category === categoryX);
          if (result[itemValue] || result[itemValue] === 0) {
            tooltipText += `<div class="chart-lr-grid center -pt-1 -font-size-12" ><p class="-color-darkgray-1 flex-ui v-center center">
                    <span class="legend-mark -mr-2" style="background-color:${this.colorFilter[itemKey]}"></span><span class="legend-text">${itemValue}</span></p>
                    <p class="-color-darkgray-1 -ml-24">${this.numberPrefix}${numberWithCommas(result[itemKey])}</p></div>`
          }
        });
        return `<div class="amchart-custom-tooltip -p-1 -pb-2">
                      <h2 class="-font-size-12 -color-darkgray-1 -font-weight-regular -pb-2">${categoryX}</h2>
                        ${tooltipText}`
                + 
                  (this.showColumnTotal? `<div class="total -color-darkgray-1 chart-lr-grid v-center -width-100 -pt-2 -mt-2 -border-gray-2">
                    <em>Total :</em>
                    <span>${this.numberPrefix}${numberWithCommas(total || '0')}</span>
                  </div>`: '')
                +
                `</div>`;
      });
      return series;
    },
    createBullet (series) {
      let bullet = series.bullets.push(new am4charts.CircleBullet())
      bullet.circle.strokeWidth = 2;
      bullet.circle.radius = 1;
      bullet.cursorOverStyle = am4core.MouseCursorStyle.pointer
      bullet.events.on( "hit",
        ev => {
          this.$emit('hit', ev)
        },
        this
      );
      return bullet
    },
    createLegned () {
      this.chart.legend = new am4charts.Legend();
      this.chart.legend.position = "bottom";
      this.chart.legend.marginLeft = 42;
      this.chart.legend.fontSize = 12;
      /*label marker styling*/
      this.chart.legend.useDefaultMarker = true;
      var marker = this.chart.legend.markers.template.children.getIndex(0);
      marker.cornerRadius(0, 0, 0, 0);

      var markerTemplate = this.chart.legend.markers.template;
      markerTemplate.width = 16;
      markerTemplate.height = 8;

      this.chart.legend.itemContainers.template.paddingTop = 0;
      this.chart.legend.itemContainers.template.paddingBottom = 0;
      if (this.numberFormatter) {
        this.chart.numberFormatter.numberFormat = this.numberFormatter.numberFormat;
        this.chart.numberFormatter.bigNumberPrefixes = this.numberFormatter.bigNumberPrefixes;
      }
    },
    startData () {
      if (this.chart) {
        this.chart.dispose();
      }
      // 스킨설정
      am4core.useTheme(am4themes_animated);
      this.chart = am4core.create(this.$refs[this.refName], am4charts.XYChart)

      // 커스터마이징 시 자동으로 클래스가 추가됨?
      am4core.options.autoSetClassName = true;

      // 차트 데이터 지정
      this.chart.data = this.chartData

      // X축은 카테고리기반 축 생성(Category axis)
      this.createCategoryAxies()
      this.createValueAxis()
      this.createTopContainer()
      this.chart.cursor = this.createCursor();

      this.colorFilter = {};
      this.seriesName = {};
      for (var i = 0; i < this.chartFilter.length; i++) {
        this.colorFilter[this.chartFilter[i]] = this.CHART_COLORS[i];
        this.seriesName[this.chartFilter[i]] = this.chartFilter[i];
        if (typeof(this.filter[i]) === 'string') {
          this.createSeries(this.filter[i], this.filter[i], CHART_COLORS[i], i);
        } else {
          this.createSeries(this.filter[i].column, this.filter[i].label, CHART_COLORS[i], i);
        }
      }
      this.createLegned()
      this.chart.events.on("ready", () => {
        this.$emit('lodingFlag', self.lodingFlag);
      });
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler () {
        this.startData()
      }
    }
  },
  mounted () {
    this.startData()
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  }
}
</script>
<style scoped>
  .resale_status_purchase.chart {
    height: 100%;
  }
</style>



// WEBPACK FOOTER //
// src/pages/components/SrlineChart.vue